import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Drawer, Typography } from "@mui/material";
import { TourProvider, useTour } from "@reactour/tour";
import Navbar from "../../layouts/Navbar/Navbar";
import { ExamCode } from "./partials/ExamCode/ExamCode";
import { RecentExams } from "./partials/RecentExams/RecentExams";
import { MyProgress, ProgressDataProps } from "./partials/MyProgress/MyProgress";
import { Sidebar } from "./partials/Sidebar/Sidebar";
import { PREBOARD_STORAGE_KEY } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { validate } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ValidateTokenData } from "../../api/interfaces/Auth";
import { getActivatedCodes } from "../../api/services/code";
import { ExamCodeResponse } from "../../api/interfaces/Code";
import { ExamHistory } from "../../api/interfaces/Exam";
import { getDashboard } from "../../api/services/dashboard";
import { DashboardProgress } from "../../api/interfaces/Dashboard";
import { setTourCompleted } from "../../api/services/user";

// ---------------- DASHBOARD ----------------
const DashboardContent: React.FC<{
    user: ValidateTokenData;
    activatedExamCodes: ExamCodeResponse[];
    recentExams: ExamHistory[];
    progress?: DashboardProgress | null;
    onSuccessActivateCode: () => void;
    isDashboardLoading: boolean;
    shouldRefetchExamCodes: boolean;
}> = ({ user, activatedExamCodes, recentExams, progress, onSuccessActivateCode, isDashboardLoading, shouldRefetchExamCodes }) => {
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);
    const { setIsOpen } = useTour();

    useEffect(() => {
        if (user && user.isTourCompleted === false) {
            setIsOpen(true);
            // Lock scroll
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
        }
    }, [user.isTourCompleted, setIsOpen]);

    return (
        <>
            <Navbar name={`${user.firstName} ${user.lastName}`} onOpenSidebar={() => setMobileSidebarOpen(true)} />
            <main
                style={{
                    display: "flex",
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor: "#f6f6f6",
                    overflow: "hidden",
                }}
            >
                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        overflowX: "hidden",
                        backgroundColor: "#EEF0FA",
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                            px: { xs: 2, sm: 3, lg: 15 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: "100%", md: "25%" },
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: { xs: "100%", md: "auto" },
                                        display: { xs: "none", md: "block" },
                                    }}
                                >
                                    <Sidebar variant="desktop" user={user} onSuccessActivateCode={onSuccessActivateCode} />
                                </Box>

                                <Drawer
                                    anchor="left"
                                    open={mobileSidebarOpen}
                                    onClose={() => setMobileSidebarOpen(false)}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 280,
                                                backgroundColor: "#F6F6F6",
                                                overflow: "hidden",

                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            overflowY: "auto",
                                            p: 2,
                                        }}
                                    >

                                        <Sidebar
                                            variant="mobile"
                                            user={user}
                                            onSuccessActivateCode={onSuccessActivateCode}
                                        />
                                    </Box>
                                </Drawer>
                            </Box>

                            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                <Box
                                    sx={{
                                        display: "grid",
                                        // gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" },
                                        gap: 3,
                                        alignItems: "stretch",
                                        overflow: "hidden",
                                    }}
                                >
                                    <ExamCode
                                        activatedExamCodes={activatedExamCodes}
                                        isLoading={isDashboardLoading || shouldRefetchExamCodes}
                                    />
                                    {/* <MyProgress
                                        progress={progress}
                                        isLoading={isDashboardLoading}
                                    /> */}
                                </Box>

                                <Box sx={{ width: "100%", overflow: "hidden" }}>
                                    <RecentExams
                                        recentExams={recentExams}
                                        isLoading={isDashboardLoading}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </main>
        </>
    );
};

// ---------------- TOUR CONTENT ----------------
const CustomTourContent: React.FC<{ totalSteps: number; onAccept: () => void }> = ({
    totalSteps,
    onAccept,
}) => {
    const { currentStep, setCurrentStep, steps, setIsOpen } = useTour();
    const isLast = currentStep === totalSteps - 1;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
                {(() => {
                    const StepContent: any = steps?.[currentStep]?.content;
                    return typeof StepContent === "function" ? <StepContent /> : StepContent;
                })()}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "#6246EA", fontWeight: 600, fontSize: "0.875rem" }}>
                    Step {currentStep + 1} of {totalSteps}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                    {currentStep > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentStep(currentStep - 1)}
                            sx={{
                                textTransform: "none",
                                borderColor: "#6246EA",
                                color: "#6246EA",
                                "&:hover": { borderColor: "#4e39c3", backgroundColor: "#f1efff" },
                            }}
                        >
                            Back
                        </Button>
                    )}
                    {!isLast ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setCurrentStep(currentStep + 1)}
                            sx={{
                                textTransform: "none",
                                backgroundColor: "#6246EA",
                                "&:hover": { backgroundColor: "#4e39c3" },
                            }}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                                onAccept();
                                setIsOpen(false);
                                document.body.style.overflow = "";
                                document.documentElement.style.overflow = "";
                            }}
                            sx={{
                                textTransform: "none",
                                backgroundColor: "#6246EA",
                                "&:hover": { backgroundColor: "#4e39c3" },
                            }}
                        >
                            Accept
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ---------------- HOME PAGE ----------------
const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const [isDashboardLoading, setIsDashboardLoading] = useState<boolean>(false);
    const [currentUser, setCurrentUser] = useState<ValidateTokenData | null>(null);
    const [activatedCodes, setActivatedCodes] = useState<ExamCodeResponse[]>([]);
    const [recentExams, setRecentExams] = useState<ExamHistory[]>([]);
    const [progress, setProgress] = useState<DashboardProgress | null>(null);
    const [shouldRefetchExamCodes, setShouldRefetchExamCodes] = useState(false);

    const refetchExams = useCallback(() => {
        setShouldRefetchExamCodes(prev => !prev);
    }, []);

    useEffect(() => {
        const run = async () => {
            if (shouldRefetchExamCodes) {
                try {
                    const getCodes = await getActivatedCodes();
                    setActivatedCodes(getCodes.data);
                } catch (error) {
                    handleApiError(error, {
                        notify: (msg) => {
                            pushNotification({
                                details: msg,
                                type: NotificationTypes.Error,
                            });
                        },
                    });
                } finally {
                    setShouldRefetchExamCodes(false);
                }
            }
        };
        run();
    }, [shouldRefetchExamCodes]);


    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(PREBOARD_STORAGE_KEY) ||
                sessionStorage.getItem(PREBOARD_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            try {
                setIsDashboardLoading(true);
                const validateResponse = await validate();
                setCurrentUser(validateResponse.data as unknown as ValidateTokenData);

                const dashboardInfo = await getDashboard();

                // const getCodes = await getActivatedCodes();
                // const recentExams = await getExamHistory({ limit: 1 });

                setActivatedCodes(dashboardInfo.data.examCodes);
                setRecentExams(dashboardInfo.data.attempts);
                setProgress(dashboardInfo.data.progress);
            } catch (error: any) {
                // trigger handleApiError is status code is not 401
                if (error.response?.status !== 401) {
                    handleApiError(error, {
                        notify: (msg) => {
                            pushNotification({
                                details: msg,
                                type: NotificationTypes.Error,
                            });
                        },
                    });
                }
            } finally {
                setIsDashboardLoading(false);
            }
        };
        run();

    }, [navigate]);

    const steps = [
        {
            selector: "#activate-code-btn",
            content: "Input the exam code.",
        },
        {
            selector: "#user-side-menu",
            content: "View profile and navigate sections.",
        },
        {
            selector: "#take-exam-section",
            content: "View activated codes and exam details.",
        },
        // {
        //     selector: "#my-progress-section",
        //     content: "Track your overall progress here.",
        // },
        {
            selector: "#recent-exams-section",
            content: "View recent exams here. To see the complete list, go to the Results section.",
        },
    ];

    const handleAccept = async () => {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        // Run in background
        setTourCompleted();
    };

    return (
        <TourProvider
            steps={steps}
            disableInteraction={true}
            disableKeyboardNavigation={true}
            onClickMask={() => { }}
            styles={{
                popover: (base) => ({
                    ...base,
                    borderRadius: 12,
                    padding: 0,
                    background: "#fff",
                    color: "#333",
                    width: 300,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                }),
                badge: (base) => ({ ...base, display: "none" }),
            }}
            ContentComponent={() => <CustomTourContent totalSteps={steps.length} onAccept={handleAccept} />}
        >
            {currentUser && (
                <DashboardContent
                    user={currentUser}
                    activatedExamCodes={activatedCodes}
                    recentExams={recentExams}
                    progress={progress}
                    onSuccessActivateCode={refetchExams}
                    isDashboardLoading={isDashboardLoading}
                    shouldRefetchExamCodes={shouldRefetchExamCodes}
                />
            )}
        </TourProvider>
    );
};

export default HomePage;
